import type { AnswersByQuestion, ContentItem } from "../layout/documentLayout";

// Pure row logic for the chart editor. A chart's cells are its descendants (columns[0], at any
// depth); each cell answer is stored with `chartRow` = the row index. Removing a row deletes every
// descendant answer in that row (mobile `notifyOnRemovingRow`), not only the direct columns (the
// classic modal), so no orphan answers stay behind.

function descendantsOf(question: ContentItem): ContentItem[] {
  const result: ContentItem[] = [];
  for (const child of question.columns?.[0] ?? []) {
    result.push(child, ...descendantsOf(child));
  }
  return result;
}

/** Sorted distinct rows that hold at least one descendant answer; [0] when there are none. */
export function chartRowIndexes(chart: ContentItem, answers: AnswersByQuestion): number[] {
  const rows = new Set<number>();
  for (const descendant of descendantsOf(chart)) {
    for (const answer of answers.get(descendant.id) ?? []) {
      rows.add(answer.chartRow);
    }
  }
  if (rows.size === 0) {
    return [0];
  }
  return [...rows].sort((a, b) => a - b);
}

/** The row index "Add row" creates (mobile getLatestRow() + 1). */
export function nextChartRow(rows: number[]): number {
  return rows.length === 0 ? 0 : Math.max(...rows) + 1;
}

/** Every stored descendant answer of `row`, in content order; each is deleted with `answer: null`. */
export function answersToClearForRow(
  chart: ContentItem,
  answers: AnswersByQuestion,
  row: number
): { questionId: number; chartRow: number }[] {
  return descendantsOf(chart)
    .filter((descendant) =>
      (answers.get(descendant.id) ?? []).some((answer) => answer.chartRow === row)
    )
    .map((descendant) => ({ questionId: descendant.id, chartRow: row }));
}

/** The answers as one chart row sees them: every question keeps only its answers in `row`. */
export function answersForRow(answers: AnswersByQuestion, row: number): AnswersByQuestion {
  const scoped: AnswersByQuestion = new Map();
  for (const [questionId, list] of answers) {
    const inRow = list.filter((answer) => answer.chartRow === row);
    if (inRow.length > 0) {
      scoped.set(questionId, inRow);
    }
  }
  return scoped;
}

/**
 * Rows the chart editor shows: the stored rows (gaps kept, e.g. [0, 2]) plus rows added in the
 * editor that hold no answer yet ("Add row" is client-only until a cell of it is saved).
 */
export function editorChartRows(
  chart: ContentItem,
  answers: AnswersByQuestion,
  addedRows: number[]
): number[] {
  const rows = new Set([...chartRowIndexes(chart, answers), ...addedRows]);
  return [...rows].sort((a, b) => a - b);
}

/**
 * "Remove last row": the last shown row and the `null` writes that delete every answer in it
 * (none for a row only added in the editor). Null when a single row is left: the chart always
 * keeps one row, like the classic form.
 */
export function removeLastRowPlan(
  chart: ContentItem,
  answers: AnswersByQuestion,
  rows: number[]
): { row: number; writes: { answer: null; chartRow: number; questionId: number }[] } | null {
  if (rows.length <= 1) {
    return null;
  }
  const row = Math.max(...rows);
  return {
    row,
    writes: answersToClearForRow(chart, answers, row).map((target) => ({
      ...target,
      answer: null,
    })),
  };
}
